"use client";

import React from "react";
import { FileText, ShieldAlert, Compass, Layers, Mail, Phone } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TermsOfService() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="termsofservice" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header Block */}
        <div style={{
        opacity: isVisible ? 1 : 0
      }} className="transition-all duration-700 ease-out translate-y-0 text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/80 border border-border text-foreground text-xs font-medium uppercase tracking-wider mb-4">
            <FileText className="size-3.5 text-chart-1" />
            <span>Document &amp; Guidelines</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground mb-4">
            Terms of Service
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Please read these terms carefully before accessing or using the Decatur Heritage Renovations digital resources, planning materials, and communication channels.
          </p>
        </div>

        {/* Core Content Grid */}
        <div style={{
        opacity: isVisible ? 1 : 0
      }} className="transition-all duration-700 delay-150 ease-out space-y-8">
          {/* Section 1: Website Usage */}
          <Card className="border border-border/80 bg-card/90 shadow-xs rounded-xl overflow-hidden backdrop-blur-xs">
            <CardHeader className="flex flex-row items-start gap-4 pb-4">
              <div className="p-2.5 rounded-lg bg-secondary text-foreground shrink-0 mt-0.5">
                <Compass className="size-5 text-chart-1" />
              </div>
              <div className="space-y-1">
                <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Section 01</span>
                <CardTitle className="text-xl sm:text-2xl font-serif text-foreground">
                  Website Usage &amp; Permitted Remodeling Planning
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-sm sm:text-base text-foreground/90 font-default leading-relaxed pt-2">
              <p>
                All informational materials, renovation project guides, layout ideas, and visual presentations hosted on this website are published solely to assist homeowners in planning residential remodel concepts in Decatur, Georgia, and adjacent local communities.
              </p>
              <p>
                Visitors are granted a limited, non-transferable, and revocable license to view, save, and print select informational pages strictly for personal, non-commercial remodeling preparation. Unauthorised reproduction, bulk scraping, commercial redistribution, or public alteration of any website text, imagery, or digital assets without prior written consent is strictly prohibited.
              </p>
            </CardContent>
          </Card>

          {/* Section 2: Estimates & Pricing Disclaimers */}
          <Card className="border border-border/80 bg-card/90 shadow-xs rounded-xl overflow-hidden backdrop-blur-xs">
            <CardHeader className="flex flex-row items-start gap-4 pb-4">
              <div className="p-2.5 rounded-lg bg-secondary text-foreground shrink-0 mt-0.5">
                <ShieldAlert className="size-5 text-chart-1" />
              </div>
              <div className="space-y-1">
                <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Section 02</span>
                <CardTitle className="text-xl sm:text-2xl font-serif text-foreground">
                  Renovation Estimates &amp; Pricing Disclaimers
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-sm sm:text-base text-foreground/90 font-default leading-relaxed pt-2">
              <p>
                Any preliminary estimates, budget ranges, ballpark calculations, or conceptual allowances discussed online, through digital forms, or via preliminary phone consultations are non-binding budgetary guidelines provided for general planning purposes only.
              </p>
              <p>
                Definitive project pricing and construction schedules are established only following an in-person on-site property evaluation, finalized architectural and finish selections, approved scopes of work, and a mutually signed formal contract between the property owner and Decatur Heritage Renovations.
              </p>
              <p className="text-muted-foreground text-xs sm:text-sm italic">
                Site conditions, structural requirements, zoning ordinances, and material availability directly influence final written proposals.
              </p>
            </CardContent>
          </Card>

          {/* Section 3: Intellectual Property */}
          <Card className="border border-border/80 bg-card/90 shadow-xs rounded-xl overflow-hidden backdrop-blur-xs">
            <CardHeader className="flex flex-row items-start gap-4 pb-4">
              <div className="p-2.5 rounded-lg bg-secondary text-foreground shrink-0 mt-0.5">
                <Layers className="size-5 text-chart-1" />
              </div>
              <div className="space-y-1">
                <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Section 03</span>
                <CardTitle className="text-xl sm:text-2xl font-serif text-foreground">
                  Intellectual Property Ownership of Plans &amp; Renderings
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-sm sm:text-base text-foreground/90 font-default leading-relaxed pt-2">
              <p>
                All architectural concepts, schematics, elevations, custom cabinetry configurations, 3D renderings, design boards, and drafting documentation created or supplied by Decatur Heritage Renovations remain the exclusive intellectual property of the company.
              </p>
              <p>
                Client possession of preliminary renderings or layout concepts does not convey ownership rights or construction rights to third-party contractors unless explicitly licensed and assigned in a written agreement.
              </p>
            </CardContent>
          </Card>

          {/* Section 4: Contact & Inquiries */}
          <div className="rounded-xl border border-border bg-secondary/50 p-6 sm:p-8">
            <h2 className="text-lg sm:text-xl font-serif font-medium text-foreground mb-3">
              Questions Regarding Terms
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground font-default mb-6 leading-relaxed">
              If you have questions concerning these website terms, design rights, or estimate guidelines, please contact our team directly through the following channels:
            </p>

            <Separator className="mb-6 bg-border" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a href="mailto:serviceleadalerts@gmail.com" className="flex items-center gap-3.5 p-3.5 rounded-lg bg-background border border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors group">
                <div className="p-2 rounded-md bg-secondary text-foreground group-hover:bg-background transition-colors shrink-0">
                  <Mail className="size-4 text-chart-1" />
                </div>
                <div className="overflow-hidden">
                  <span className="text-xs text-muted-foreground block font-medium">Direct Email</span>
                  <span className="text-sm font-medium truncate block">serviceleadalerts@gmail.com</span>
                </div>
              </a>

              <a href="tel:+14708479076" className="flex items-center gap-3.5 p-3.5 rounded-lg bg-background border border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors group">
                <div className="p-2 rounded-md bg-secondary text-foreground group-hover:bg-background transition-colors shrink-0">
                  <Phone className="size-4 text-chart-1" />
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block font-medium">Telephone</span>
                  <span className="text-sm font-medium block">(470) 847-9076</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}